import { createAnalysisState, validateAnalysisState } from '@aibi/analysis-engine';
import type {
  AnalysisState,
  FilterOperator,
  ScalarValue,
  TimeGrain,
  VisualizationType,
} from '@aibi/contracts';
import {
  addField,
  addFilter,
  clearSelections,
  drillDown,
  drillUp,
  moveField,
  removeField,
  removeFilter,
  setGrouping,
  setSort,
  setTimeGrain,
  toggleSelection,
  updateFilter,
  type CommandContext,
  type FilterDraft,
} from './analysis-commands';
import type { FieldKind } from './field-catalog';
import type { Rejection, StepResult } from './rejection';
import {
  normalizeCard,
  setVisualizationType,
  updateVisualization,
  type VisualizationPatch,
} from './visualization-commands';
import {
  cardHeight,
  cardRect,
  cardWidth,
  clampCardHeight,
  clampCardWidth,
  clampCardX,
  clampCardY,
  DEFAULT_CARD_HEIGHT,
  DEFAULT_CARD_WIDTH,
  findAdjacentPosition,
  findFreePosition,
  isRectFree,
  MAX_CARDS,
  nextCardId,
  normalizePositions,
  sameContent,
  withPosition,
  type WorkspaceDocument,
} from './workspace-document';

/**
 * Comandos del workspace. Son la unica forma de modificar el documento: cada interaccion
 * de UI (clic, arrastre, teclado, IA en el futuro) se traduce a uno de estos comandos.
 */
export type WorkspaceCommand =
  | {
      readonly type: 'add-card';
      readonly visualization?: VisualizationType;
      readonly fieldIds?: readonly string[];
    }
  | { readonly type: 'duplicate-card'; readonly cardId: string; readonly afterCardId?: string }
  | { readonly type: 'remove-card'; readonly cardId: string }
  | { readonly type: 'move-card'; readonly from: number; readonly to: number }
  | {
      readonly type: 'place-card';
      readonly cardId: string;
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly type: 'resize-card';
      readonly cardId: string;
      readonly width?: number;
      readonly height?: number;
      readonly x?: number;
      readonly y?: number;
    }
  | { readonly type: 'rename-card'; readonly cardId: string; readonly title: string }
  | {
      readonly type: 'set-card-density';
      readonly cardId: string;
      readonly density: 'compact' | 'comfortable';
    }
  | { readonly type: 'rename-workspace'; readonly name: string }
  | {
      readonly type: 'add-field';
      readonly cardId: string;
      readonly fieldId: string;
      readonly position?: number;
      readonly expectedKind?: FieldKind;
    }
  | { readonly type: 'remove-field'; readonly cardId: string; readonly key: string }
  | {
      readonly type: 'move-field';
      readonly cardId: string;
      readonly kind: FieldKind;
      readonly from: number;
      readonly to: number;
    }
  | {
      readonly type: 'set-time-grain';
      readonly cardId: string;
      readonly key: string;
      readonly grain: TimeGrain | null;
    }
  | { readonly type: 'add-filter'; readonly cardId: string; readonly draft: FilterDraft }
  | {
      readonly type: 'update-filter';
      readonly cardId: string;
      readonly filterId: string;
      readonly operator: FilterOperator;
      readonly values: readonly ScalarValue[];
    }
  | { readonly type: 'remove-filter'; readonly cardId: string; readonly filterId: string }
  | {
      readonly type: 'set-sort';
      readonly cardId: string;
      readonly key: string;
      readonly direction: 'asc' | 'desc' | null;
    }
  | {
      readonly type: 'set-grouping';
      readonly cardId: string;
      readonly grandTotal?: boolean;
      readonly subtotals?: boolean;
    }
  | {
      readonly type: 'set-visualization-type';
      readonly cardId: string;
      readonly visualization: VisualizationType;
    }
  | {
      readonly type: 'update-visualization';
      readonly cardId: string;
      readonly patch: VisualizationPatch;
    }
  | {
      readonly type: 'toggle-selection';
      readonly cardId: string;
      readonly dimensionId: string;
      readonly value: ScalarValue;
      readonly additive: boolean;
    }
  | { readonly type: 'clear-selections'; readonly dimensionId?: string }
  | {
      readonly type: 'set-interaction';
      readonly cardId: string;
      readonly interaction: Partial<AnalysisState['interaction']>;
    }
  | {
      readonly type: 'drill-down';
      readonly cardId: string;
      readonly key: string;
      readonly value: ScalarValue;
    }
  | { readonly type: 'drill-up'; readonly cardId: string; readonly hierarchyId: string }
  | {
      /**
       * Aplica una propuesta del asistente (ya validada por el AI Core) como un unico paso
       * del historial. `baseState` es la tarjeta sobre la que se calculo: si la tarjeta
       * cambio desde entonces, la propuesta es obsoleta y se rechaza. `cardId = null`
       * crea una tarjeta nueva con el estado propuesto.
       */
      readonly type: 'apply-ai-proposal';
      readonly cardId: string | null;
      readonly baseState: AnalysisState | null;
      readonly state: AnalysisState;
    };

export interface WorkspaceOutcome {
  /** Documento resultante; es la MISMA referencia que la entrada si hubo rechazo. */
  readonly document: WorkspaceDocument;
  readonly rejection: Rejection | null;
  /** Tarjeta creada por el comando (para seleccionarla en la UI). */
  readonly createdCardId: string | null;
}

const rejected = (document: WorkspaceDocument, rejection: Rejection): WorkspaceOutcome => ({
  document,
  rejection,
  createdCardId: null,
});

/**
 * Aplica un comando de forma pura y determinista. Cada tarjeta modificada se normaliza
 * (encodings y referencias) y se revalida contra el Semantic Model: si el resultado no es
 * coherente, el comando se rechaza y el documento no cambia.
 */
export function applyWorkspaceCommand(
  document: WorkspaceDocument,
  command: WorkspaceCommand,
  context: CommandContext,
): WorkspaceOutcome {
  const commit = (
    cards: AnalysisState[],
    createdCardId: string | null = null,
  ): WorkspaceOutcome => ({
    document: { ...document, cards: normalizePositions(cards) },
    rejection: null,
    createdCardId,
  });

  /** Aplica un paso a una tarjeta, la normaliza y la valida. */
  const onCard = (cardId: string, step: (card: AnalysisState) => StepResult): WorkspaceOutcome => {
    const position = document.cards.findIndex((card) => card.id === cardId);
    const card = document.cards[position];
    if (!card)
      return rejected(document, { code: 'unknown-card', message: 'La tarjeta no existe.' });
    const stepped = step(card);
    if (stepped.ok && stepped.state === card) {
      return { document, rejection: null, createdCardId: null };
    }
    const result = finalize(stepped, context);
    if (!result.ok) return rejected(document, result.rejection);
    const cards = [...document.cards];
    cards[position] = result.state;
    return commit(cards);
  };

  switch (command.type) {
    case 'add-card': {
      if (document.cards.length >= MAX_CARDS) {
        return rejected(document, {
          code: 'limit-reached',
          message: `El workspace admite como maximo ${String(MAX_CARDS)} visualizaciones.`,
        });
      }
      const id = nextCardId(document.cards);
      const slot = findFreePosition(document.cards, DEFAULT_CARD_WIDTH, DEFAULT_CARD_HEIGHT);
      let card: AnalysisState = {
        ...createAnalysisState({
          id,
          semanticModelId: document.semanticModelId,
          visualization: command.visualization ?? 'table',
        }),
      };
      card = {
        ...withPosition(card, slot),
        selections: document.cards[0]?.selections ?? [],
      };
      for (const fieldId of command.fieldIds ?? []) {
        const step = addField(card, context, { fieldId });
        if (!step.ok) return rejected(document, step.rejection);
        card = step.state;
      }
      const result = finalize({ ok: true, state: card }, context);
      if (!result.ok) return rejected(document, result.rejection);
      return commit([...document.cards, result.state], id);
    }
    case 'duplicate-card': {
      const position = document.cards.findIndex((card) => card.id === command.cardId);
      const source = document.cards[position];
      if (!source)
        return rejected(document, { code: 'unknown-card', message: 'La tarjeta no existe.' });
      if (document.cards.length >= MAX_CARDS) {
        return rejected(document, {
          code: 'limit-reached',
          message: `El workspace admite como maximo ${String(MAX_CARDS)} visualizaciones.`,
        });
      }
      const id = nextCardId(document.cards);
      const afterId = command.afterCardId ?? command.cardId;
      const anchorCard =
        document.cards.find((card) => card.id === afterId) ?? source;
      const slot = findAdjacentPosition(
        document.cards,
        cardWidth(source),
        cardHeight(source),
        cardRect(anchorCard),
      );
      const copy: AnalysisState = withPosition(structuredClone({ ...source, id }), slot);
      const afterIndex = document.cards.findIndex((card) => card.id === afterId);
      const insertAt = (afterIndex >= 0 ? afterIndex : position) + 1;
      const cards = [...document.cards];
      cards.splice(insertAt, 0, copy);
      return commit(cards, id);
    }
    case 'remove-card': {
      const cards = document.cards.filter((card) => card.id !== command.cardId);
      if (cards.length === document.cards.length) {
        return rejected(document, { code: 'unknown-card', message: 'La tarjeta no existe.' });
      }
      return commit(cards);
    }
    case 'move-card': {
      const { from, to } = command;
      const size = document.cards.length;
      if (from < 0 || from >= size || to < 0 || to >= size) {
        return rejected(document, { code: 'invalid-move', message: 'Posicion fuera de rango.' });
      }
      if (from === to) return { document, rejection: null, createdCardId: null };
      const cards = [...document.cards];
      const [moved] = cards.splice(from, 1);
      if (moved) cards.splice(to, 0, moved);
      return commit(cards);
    }
    case 'place-card': {
      return onCard(command.cardId, (card) => {
        const width = cardWidth(card);
        const height = cardHeight(card);
        const desired = {
          x: clampCardX(command.x, width),
          y: clampCardY(command.y),
          w: width,
          h: height,
        };
        const occupied = document.cards
          .filter((other) => other.id !== card.id)
          .map(cardRect);
        const next = isRectFree(occupied, desired)
          ? desired
          : findFreePosition(document.cards, width, height, desired, card.id);
        const current = cardRect(card);
        if (
          current.x === next.x &&
          current.y === next.y &&
          current.w === next.w &&
          current.h === next.h
        ) {
          return { ok: true, state: card };
        }
        return { ok: true, state: withPosition(card, next) };
      });
    }
    case 'resize-card': {
      if (
        command.width === undefined &&
        command.height === undefined &&
        command.x === undefined &&
        command.y === undefined
      ) {
        return { document, rejection: null, createdCardId: null };
      }
      return onCard(command.cardId, (card) => {
        const current = cardRect(card);
        const width = clampCardWidth(command.width ?? current.w);
        const height = clampCardHeight(command.height ?? current.h);
        const x = clampCardX(command.x ?? current.x, width);
        const y = clampCardY(command.y ?? current.y);
        if (width === current.w && height === current.h && x === current.x && y === current.y) {
          return { ok: true, state: card };
        }
        const desired = { x, y, w: width, h: height };
        const occupied = document.cards
          .filter((other) => other.id !== card.id)
          .map(cardRect);
        const next = isRectFree(occupied, desired)
          ? desired
          : findFreePosition(document.cards, width, height, desired, card.id);
        return { ok: true, state: withPosition(card, next) };
      });
    }
    case 'rename-card':
      return onCard(command.cardId, (card) => {
        const title = command.title.trim().slice(0, 200);
        const layout = { ...card.layout };
        if (title === '') Reflect.deleteProperty(layout, 'title');
        else layout.title = title;
        return { ok: true, state: { ...card, layout } };
      });
    case 'set-card-density':
      return onCard(command.cardId, (card) => {
        if (card.layout.density === command.density) return { ok: true, state: card };
        return {
          ok: true,
          state: { ...card, layout: { ...card.layout, density: command.density } },
        };
      });
    case 'rename-workspace': {
      const name = command.name.trim().slice(0, 200);
      if (name === '') {
        return rejected(document, {
          code: 'invalid-state',
          message: 'El nombre no puede quedar vacio.',
        });
      }
      return { document: { ...document, name }, rejection: null, createdCardId: null };
    }
    case 'add-field':
      return onCard(command.cardId, (card) =>
        addField(card, context, {
          fieldId: command.fieldId,
          ...(command.position === undefined ? {} : { position: command.position }),
          ...(command.expectedKind === undefined ? {} : { expectedKind: command.expectedKind }),
        }),
      );
    case 'remove-field':
      return onCard(command.cardId, (card) => removeField(card, context, command.key));
    case 'move-field':
      return onCard(command.cardId, (card) =>
        moveField(card, command.kind, command.from, command.to),
      );
    case 'set-time-grain':
      return onCard(command.cardId, (card) =>
        setTimeGrain(card, context, command.key, command.grain),
      );
    case 'add-filter':
      return onCard(command.cardId, (card) => addFilter(card, context, command.draft));
    case 'update-filter':
      return onCard(command.cardId, (card) =>
        updateFilter(card, context, command.filterId, {
          operator: command.operator,
          values: command.values,
        }),
      );
    case 'remove-filter':
      return onCard(command.cardId, (card) => removeFilter(card, command.filterId));
    case 'set-sort':
      return onCard(command.cardId, (card) => setSort(card, command.key, command.direction));
    case 'set-grouping':
      return onCard(command.cardId, (card) =>
        setGrouping(card, {
          ...(command.grandTotal === undefined ? {} : { grandTotal: command.grandTotal }),
          ...(command.subtotals === undefined ? {} : { subtotals: command.subtotals }),
        }),
      );
    case 'set-visualization-type':
      return onCard(command.cardId, (card) => setVisualizationType(card, command.visualization));
    case 'update-visualization':
      return onCard(command.cardId, (card) => updateVisualization(card, command.patch));
    case 'toggle-selection':
      return applySelections(document, context, command.cardId, (selections, origin) =>
        toggleSelection(selections, {
          dimensionId: command.dimensionId,
          value: command.value,
          additive: command.additive,
          mode: origin.interaction.selectionMode,
        }),
      );
    case 'clear-selections':
      return applySelections(document, context, null, (selections) =>
        clearSelections(selections, command.dimensionId),
      );
    case 'set-interaction':
      return onCard(command.cardId, (card) => ({
        ok: true,
        state: { ...card, interaction: { ...card.interaction, ...command.interaction } },
      }));
    case 'drill-down':
      return onCard(command.cardId, (card) => drillDown(card, context, command.key, command.value));
    case 'drill-up':
      return onCard(command.cardId, (card) => drillUp(card, command.hierarchyId));
    case 'apply-ai-proposal':
      return applyProposal(document, command, context, commit);
  }
}

function applyProposal(
  document: WorkspaceDocument,
  command: Extract<WorkspaceCommand, { type: 'apply-ai-proposal' }>,
  context: CommandContext,
  commit: (cards: AnalysisState[], createdCardId?: string | null) => WorkspaceOutcome,
): WorkspaceOutcome {
  if (command.state.semanticModelId !== document.semanticModelId) {
    return rejected(document, {
      code: 'invalid-state',
      message: 'La propuesta corresponde a otro modelo semantico.',
    });
  }
  if (command.cardId === null) {
    if (document.cards.length >= MAX_CARDS) {
      return rejected(document, {
        code: 'limit-reached',
        message: `El workspace admite como maximo ${String(MAX_CARDS)} visualizaciones.`,
      });
    }
    const id = nextCardId(document.cards);
    const card: AnalysisState = {
      ...command.state,
      id,
      layout: {
        ...command.state.layout,
        position: { x: 0, y: 0, w: DEFAULT_CARD_WIDTH, h: DEFAULT_CARD_HEIGHT },
      },
      selections: document.cards[0]?.selections ?? command.state.selections,
    };
    const result = finalize({ ok: true, state: card }, context);
    if (!result.ok) return rejected(document, result.rejection);
    return commit([...document.cards, result.state], id);
  }
  const position = document.cards.findIndex((card) => card.id === command.cardId);
  const current = document.cards[position];
  if (!current)
    return rejected(document, { code: 'unknown-card', message: 'La tarjeta no existe.' });
  if (command.baseState === null || !sameContent(current, command.baseState)) {
    return rejected(document, {
      code: 'stale-proposal',
      message:
        'La visualizacion cambio desde que se genero la propuesta. Vuelva a preguntar al asistente.',
    });
  }
  const result = finalize(
    { ok: true, state: { ...command.state, id: current.id, layout: current.layout } },
    context,
  );
  if (!result.ok) return rejected(document, result.rejection);
  const cards = [...document.cards];
  cards[position] = result.state;
  return commit(cards);
}

/**
 * Selecciones asociativas: se calculan a partir de la tarjeta de origen y se escriben en
 * `selections` de esa tarjeta y de todas las que tienen `interaction.crossFilter`.
 * `originId = null` aplica a todas las tarjetas (p. ej. limpiar selecciones).
 */
function applySelections(
  document: WorkspaceDocument,
  context: CommandContext,
  originId: string | null,
  compute: (
    selections: AnalysisState['selections'],
    origin: AnalysisState,
  ) => AnalysisState['selections'],
): WorkspaceOutcome {
  const origin =
    originId === null ? document.cards[0] : document.cards.find((card) => card.id === originId);
  if (!origin) {
    return originId === null
      ? { document, rejection: null, createdCardId: null }
      : rejected(document, { code: 'unknown-card', message: 'La tarjeta no existe.' });
  }
  const selections = compute(origin.selections, origin);
  const cards: AnalysisState[] = [];
  for (const card of document.cards) {
    const affected = originId === null || card.id === originId || card.interaction.crossFilter;
    if (!affected) {
      cards.push(card);
      continue;
    }
    const result = finalize({ ok: true, state: { ...card, selections } }, context);
    if (!result.ok) return rejected(document, result.rejection);
    cards.push(result.state);
  }
  return { document: { ...document, cards }, rejection: null, createdCardId: null };
}

/** Normaliza y valida una tarjeta tras un paso aceptado. */
function finalize(step: StepResult, context: CommandContext): StepResult {
  if (!step.ok) return step;
  const normalized = normalizeCard(step.state);
  const validated = validateAnalysisState(normalized, context.index);
  if (!validated.ok) {
    const detail = validated.error.details[0];
    return {
      ok: false,
      rejection: {
        code: 'invalid-state',
        message: detail ? detail.message : validated.error.message,
      },
    };
  }
  return { ok: true, state: normalized };
}
